import {Injectable} from '@angular/core';
import {Log, User, UserManager, UserManagerSettings, WebStorageStateStore} from 'oidc-client-ts';
import {BehaviorSubject, Observable} from 'rxjs';
import {environment} from '../environments/environment';

Log.setLogger(console);

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private userManager: UserManager;
  private userSubject = new BehaviorSubject<User | null>(null);
  private isInitialized = false;
  private initializationPromise: Promise<void> | null = null;
  public readonly user$: Observable<User | null> = this.userSubject.asObservable();

  constructor() {
    const settings: UserManagerSettings = {
      ...(environment.oidc),
      userStore: new WebStorageStateStore({store: window.localStorage})
    };
    this.userManager = new UserManager(settings);
    this.setupEventHandlers();
    this.initialize().then();
  }

  private setupEventHandlers(): void {

    // Handle user loaded event
    this.userManager.events.addUserLoaded((user: User) => {
      this.userSubject.next(user);
    });

    // Handle user unloaded event
    this.userManager.events.addUserUnloaded(() => {
      this.userSubject.next(null);
    });

    // Handle silent renewal error
    this.userManager.events.addSilentRenewError((error) => {
      console.error('Silent renewal error:', error);
    });

    // Handle access token expiring
    this.userManager.events.addAccessTokenExpiring(() => {
      console.log('Access token expiring...');
    });

    // Handle access token expired
    this.userManager.events.addAccessTokenExpired(() => {
      console.log('Access token expired');
      this.userSubject.next(null);
    });
  }

  private async initialize(): Promise<void> {
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = this.loadStoredUser();
    await this.initializationPromise;
    this.isInitialized = true;
  }

  private async loadStoredUser(): Promise<void> {
    try {
      // This automatically loads from localStorage if available
      const user = await this.userManager.getUser();

      if (user && !user.expired) {
        this.userSubject.next(user);
      } else if (user && user.expired) {
        await this.userManager.removeUser();
        this.userSubject.next(null);
      } else {
        this.userSubject.next(null);
      }
    } catch (error) {
      console.error('Error loading stored user:', error);
      this.userSubject.next(null);
    }
  }

  // Ensure service is initialized before performing operations
  public async ensureInitialized(): Promise<void> {
    if (!this.isInitialized) {
      await this.initialize();
    }
  }

  async login(): Promise<void> {
    try {
      await this.ensureInitialized();
      await this.userManager.signinRedirect();
    } catch (error) {
      console.error('Login error:', error);
    }
  }

  async logout(): Promise<void> {
    try {
      await this.ensureInitialized();
      const idToken = this.getIdToken()
      await this.userManager.signoutRedirect({
        post_logout_redirect_uri: environment.oidc.post_logout_redirect_uri,
        ...(idToken ? { id_token_hint: idToken } : {}),
        extraQueryParams: {
          client_id: environment.oidc.client_id,
          redirect_uri: environment.oidc.post_logout_redirect_uri,
          response_type: environment.oidc.response_type
        }
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
  }

  async handleCallback(): Promise<User | null> {
    try {
      const user = await this.userManager.signinRedirectCallback();
      this.userSubject.next(user);
      return user;
    } catch (error) {
      console.error('Callback error:', error);
      return null;
    }
  }

  get isAuthenticated(): boolean {
    const user = this.userSubject.value;
    return !!user && !user.expired;
  }

  getAccessToken(): string | null {
    const user = this.userSubject.value;
    return user?.access_token || null;
  }

  getIdToken(): string | null {
    return this.userSubject.value?.id_token || null;
  }

  getCurrentUser(): User | null {
    return this.userSubject.value;
  }

  // Method to manually refresh the user from storage
  async refreshUser(): Promise<User | null> {
    try {
      const user = await this.userManager.getUser();
      this.userSubject.next(user);
      return user;
    } catch (error) {
      console.error('Error refreshing user:', error);
      return null;
    }
  }

  userHasRole(role: string) {
    return this.roles.includes(role);
  }

  get roles(): string[] {
    const roles: string = (this.userSubject.value?.profile?.['custom:roles'] ?? '') as string;
    return roles.split(',')
  }
}
